<template>
  <div class="detail-page">
    <AppHeader />
    <SkeletonScreen v-if="loading" />
    <template v-else-if="article.id">
      <article class="detail-article">
        <h1 class="detail-title">{{ article.title }}</h1>
        <div class="detail-meta">
          <span class="detail-meta__author">{{ article.author }}</span>
          <span class="detail-meta__time">{{ article.time }}</span>
          <span class="detail-meta__source">来源：{{ article.source.split('|')[0] }}</span>
        </div>
        <!-- <img v-if="article.image" class="detail-image" :src="article.image" alt="新闻配图" /> -->
        <div class="detail-content" v-html="article.content"></div>

        <!-- 组图（newsImages 不为空时展示） -->
        <section class="detail-gallery" v-if="article.newsImages.length">
          <div
            class="detail-gallery__stage"
            @touchstart="onGalleryTouchStart"
            @touchend="onGalleryTouchEnd"
          >
            <ul class="detail-gallery__track" :style="galleryTrackStyle">
              <li class="detail-gallery__slide" v-for="(image, idx) in article.newsImages" :key="image.id || idx">
                <img class="detail-gallery__image" :src="image.imageUrl" :alt="image.imageAlt || ''" />
              </li>
            </ul>
            <template v-if="article.newsImages.length > 1">
              <span class="detail-gallery__arrow detail-gallery__arrow--prev" @click="prevGalleryImage">‹</span>
              <span class="detail-gallery__arrow detail-gallery__arrow--next" @click="nextGalleryImage">›</span>
            </template>
          </div>
          <div class="detail-gallery__caption" v-if="currentGalleryImage && currentGalleryImage.imageDesc" v-html="currentGalleryImage.imageDesc"></div>
          <div class="detail-gallery__pager" v-if="article.newsImages.length > 1">
            <span class="detail-gallery__pager-arrow" @click="prevGalleryImage">‹</span>
            <span class="detail-gallery__pager-text">{{ galleryIndex + 1 }} / {{ article.newsImages.length }}</span>
            <span class="detail-gallery__pager-arrow" @click="nextGalleryImage">›</span>
          </div>
        </section>
        
        <!-- 信息发布和审核人员 -->
        <div class="detail-publish-info" v-if="article.publisherName || article.approvalOneBy || article.approvalTwoBy || article.approvalThreeBy">
          <span class="detail-publish-info__item" v-if="article.publisherName">
            {{ article.publisherPostName }}：{{ article.publisherName }}
          </span>
          <span class="detail-publish-info__divider" v-if="article.approvalOneBy">｜</span>
          <span class="detail-publish-info__item" v-if="article.approvalOneBy">
            一审：{{ article.approvalOneBy }}
          </span>
          <span class="detail-publish-info__divider" v-if="article.approvalTwoBy">｜</span>
          <span class="detail-publish-info__item" v-if="article.approvalTwoBy">
            二审：{{ article.approvalTwoBy }}
          </span>
          <span class="detail-publish-info__divider" v-if="article.approvalThreeBy">｜</span>
          <span class="detail-publish-info__item" v-if="article.approvalThreeBy">
            三审：{{ article.approvalThreeBy }}
          </span>
        </div>
      </article>
      <section class="detail-comments">
        <h2 class="detail-comments__title">热评论（{{ commentTotal }}）</h2>
        <CommentItem v-for="(comment, idx) in comments" :key="idx" :comment="comment" :is-logged-in="isLoggedIn" @reply="onReplyComment" />
      </section>
    </template>

    <CommentInput
      v-if="!loading"
      :style="{ bottom: commentBottom + 'px' }"
      :like-count="article.likes"
      :is-liked="article.isLiked"
      :is-favorited="article.isFavorited"
      :is-logged-in="isLoggedIn"
      :user-avatar="userInfo.avatar"
      @send="onSendComment"
      @login="goLogin"
      @like="onLikeArticle"
      @favorite="onFavoriteArticle"
      @share="onShare"
    />

    <div v-if="showShareGuide" class="share-guide-overlay" @click="closeShareGuide">
      <div class="share-guide-arrow-wrapper">
        <div class="share-guide-arrow"></div>
        <div class="share-guide-text">点击 <span class="share-guide-icon">···</span> 分享</div>
      </div>
      <div class="share-guide-tip">点击空白处关闭</div>
    </div>
  </div>
</template>

<script>
import AppHeader from '../home/components/AppHeader.vue';
import CommentItem from './components/CommentItem.vue';
import CommentInput from './components/CommentInput.vue';
import SkeletonScreen from './components/SkeletonScreen.vue';
import { getNewsContent, toggleLike, toggleFavorite } from '../../api/news';
import { getCommentList, addComment, replyComment } from '../../api/comment';
import { getUserInfo } from '../../api/member';

const DEFAULT_SHARE_IMAGE = 'https://wxcdn.cn/uploadfile/2020/0512/20200512043359781.jpg';
const IMG = (name) => require(`./assets/img/${name}`);

export default {
  name: 'NewsDetail',
  components: { AppHeader, CommentItem, CommentInput, SkeletonScreen },
  data() {
    return {
      shareItems: [
        { name: '朋友圈', icon: IMG('pengyouquan.jpg') },
        { name: '微信', icon: IMG('weixin1.png') },
        { name: '微博', icon: IMG('weibo.png') },
        { name: 'QQ', icon: IMG('qq.png') }
      ],
      article: { title: '', author: '', time: '', source: '', likes: 0, isLiked: false, isFavorited: false, image: '', content: '', summary: '', publisherName: '', approvalOneBy: '', approvalTwoBy: '', approvalThreeBy: '', publisherPostName: '', newsImages: [] },
      comments: [],
      commentTotal: 0,
      loading: false,
      userInfo: { nickname: '', avatar: '' },
      wxConfigReady: false,
      showShareGuide: false,
      shareDataSet: false,
      commentBottom: 0,
      galleryIndex: 0,
      galleryTouch: null
    };
  },
  computed: {
    isLoggedIn() { return !!this.userInfo.nickname; },
    currentGalleryImage() { return this.article.newsImages[this.galleryIndex] || null; },
    galleryTrackStyle() {
      // 每张 slide 宽度 = 100% 轨道宽度，因此切换一张就是位移 100%
      if (!this.article.newsImages.length) return {};
      return { transform: `translateX(-${this.galleryIndex * 100}%)` };
    }
  },
  created() {
    this.fetchUserInfo();
    this.fetchArticle();
  },
  mounted() {
    this.initWechatJSSDK();
    this.initViewportBottom();
  },
  beforeDestroy() { this.destroyViewportBottom(); },
  /* global wx */
  methods: {
    initViewportBottom() {
      this.updateCommentBottom();
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.updateCommentBottom);
        window.visualViewport.addEventListener('scroll', this.updateCommentBottom);
      }
      window.addEventListener('resize', this.updateCommentBottom);
    },
    destroyViewportBottom() {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.updateCommentBottom);
        window.visualViewport.removeEventListener('scroll', this.updateCommentBottom);
      }
      window.removeEventListener('resize', this.updateCommentBottom);
    },
    updateCommentBottom() {
      var vv = window.visualViewport;
      if (!vv) { this.commentBottom = 0; return; }
      var layoutH = window.innerHeight;
      var visibleH = vv.height;
      var offsetTop = vv.offsetTop;
      var bottom = layoutH - visibleH - offsetTop;
      this.commentBottom = Math.max(0, Math.round(bottom));
    },

    async initWechatJSSDK() {
      if (!this.isWeChat()) return;
      if (this.wxConfigReady) return;
      await this.loadWxScript();
      await this.configWxJSSDK();
    },

    loadWxScript() {
      return new Promise((resolve) => {
        if (typeof wx !== 'undefined') { resolve(); return; }
        const script = document.createElement('script');
        script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';
        script.onload = resolve;
        script.onerror = () => { console.error('微信JSSDK加载失败'); resolve(); };
        document.head.appendChild(script);
      });
    },

    async configWxJSSDK() {
      try {
        const pageUrl = this.getCurrentShareLink();
        const configData = await this.getJsSDKConfig(pageUrl);
        const appId = configData.appId || configData.appid || process.env.VUE_APP_WECHAT_APPID || '';
        console.log('前端使用的 appId:', appId);
        console.log('后端返回的完整 configData:', configData);
        wx.config({
          debug: false, // 生产环境请改 false
          appId: appId,
          timestamp: configData.timestamp,
          nonceStr: configData.nonceStr || configData.nonce,
          signature: configData.signature,
          // 【重点：只注册新版分享接口】
          jsApiList: ['updateAppMessageShareData', 'updateTimelineShareData']
        });

        wx.ready(() => {
          this.wxConfigReady = true;
          console.log('✅ 微信JS-SDK配置成功');
          // 注意：这里不再调用 doForceSetWechatShareData。
          // 分享数据必须等文章真实数据加载完成后再设置，
          // 否则会先用兜底标题（"新闻详情"）覆盖一次，
          // 微信采纳兜底数据后，真实的分享卡片就不再生效了。
        });

        wx.error((res) => {
          console.error('❌ 微信JS-SDK配置失败:', res);
          this.wxConfigReady = false;
          // config 失败时调用分享接口无效，不再调用
        });
      } catch (err) {
        console.error('获取微信配置失败:', err);
      }
    },

    // 强制更新分享数据（必须等文章真实数据就绪后再调用）
    doForceSetWechatShareData() {
      if (typeof wx === 'undefined') return;
      if (!this.wxConfigReady) return;

      // 【关键守卫】数据未就绪时不设置分享，避免兜底标题覆盖真实标题
      const title = this.article.title;
      if (!title || title === '新闻详情') {
        console.log('⏸️ 文章数据未就绪，暂不设置分享');
        return;
      }

      const link = this.getCurrentShareLink();
      const desc = this.getShareDescription();
      const imgUrl = this.getShareImage();

      // 加一个很小的延时，确保手机端微信的原生嗅探先完成
      setTimeout(() => {
        wx.updateAppMessageShareData({
          title: title,
          desc: desc,
          link: link,
          imgUrl: imgUrl,
          success: function () { console.log('✅ 强制更新“发送朋友”卡片成功'); },
          fail: function(err) { console.warn('⚠️ 更新失败 (可忽略)', err); }
        });

        wx.updateTimelineShareData({
          title: title,
          link: link,
          imgUrl: imgUrl,
          success: function () { console.log('✅ 强制更新“朋友圈”卡片成功'); },
          fail: function(err) { console.warn('⚠️ 更新失败 (可忽略)', err); }
        });
        this.shareDataSet = true;
      }, 300);
    },

    getShareDescription() {
      if (this.article.summary) {
        const text = this.article.summary.replace(/<[^>]+>/g, '').trim();
        return text.length > 100 ? text.substring(0, 100) + '...' : text;
      }
      if (this.article.content) {
        const text = this.article.content.replace(/<[^>]+>/g, '').trim();
        return text.length > 100 ? text.substring(0, 100) + '...' : text;
      }
      return '点击查看更多精彩内容';
    },

    getShareImage() {
      const image = this.article.image;
      if (!image) return DEFAULT_SHARE_IMAGE;
      if (image.startsWith('https://')) return image;
      if (image.startsWith('http://')) return image.replace('http://', 'https://');
      if (image.startsWith('/')) {
        const origin = window.location.origin;
        return `${origin}${image}`;
      }
      return DEFAULT_SHARE_IMAGE;
    },

    // 核心：强制替换局域网IP为公网域名，保证微信抓取的是外网
    getCurrentShareLink() {
      let url = window.location.href;
      const hashIndex = url.indexOf('#');
      if (hashIndex > -1) url = url.substring(0, hashIndex);
   
      return url;
    },

    onShare() {
      if (this.isWeChat()) {
        this.showShareGuide = true;
        // 点击分享时重新设置分享数据（若已就绪）
        if (this.wxConfigReady && this.article.title) {
           this.doForceSetWechatShareData();
        } else {
           this.initWechatJSSDK();
        }
      } else {
        // 针对手机浏览器（非微信）调用系统原生分享，如 Chrome
        if (navigator.share) {
          navigator.share({
            title: this.article.title,
            text: this.getShareDescription(),
            url: window.location.href
          }).catch(() => {});
        } else {
          this.copyLink();
        }
      }
    },

    closeShareGuide() { this.showShareGuide = false; },

    isWeChat() {
      const ua = navigator.userAgent.toLowerCase();
      return /micromessenger/.test(ua);
    },

    isIOS() {
      const ua = navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(ua);
    },

    getJsSDKConfig(pageUrl) {
      return new Promise((resolve, reject) => {
        const apiUrl = process.env.VUE_APP_API_BASE_URL;
        fetch(`${apiUrl}/api/wechat/js_ticket/serve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pageUrl })
        })
          .then(res => res.json())
          .then(data => {
            if (data.code === 200 && data.data) resolve(data.data);
            else reject(new Error(data.message || '获取配置失败'));
          })
          .catch(reject);
      });
    },

    copyLink() {
      const link = this.getCurrentShareLink();
      if (navigator.clipboard) {
        navigator.clipboard.writeText(link).then(() => alert('链接已复制到剪贴板')).catch(() => this.fallbackCopy(link));
      } else {
        this.fallbackCopy(link);
      }
    },
    fallbackCopy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); alert('链接已复制到剪贴板'); } catch (err) { console.error('复制失败:', err); alert('复制失败，请手动复制链接'); }
      document.body.removeChild(textarea);
    },

    getNewsCode() { return this.$route.params.id || this.$route.query.id || this.$route.query.newsCode || ''; },

    async fetchArticle() {
      const newsCode = this.getNewsCode();
      if (!newsCode) { this.$router.replace({ path: '/404' }); return; }
      this.loading = true;
      let redirected = false;
      try {
        const params = { newsCode };
        const codeToken = this.$route.query.codeToken || this.$route.params.codeToken;
        if (codeToken) params.codeToken = codeToken;
        const res = await getNewsContent(params);
        const data = res.data || {};
        if (!data || !data.id) { this.$router.replace({ path: '/404' }); return; }

        // 外链文章：turnUrlEnabled = 1 时直接跳转 turnUrl；为 0 或地址为空时正常展示详情
        const turnUrl = this.getTurnUrl(data);
        if (turnUrl) {
          redirected = true;
          window.location.replace(turnUrl);
          return;
        }

        this.article = {
          title: data.title || '',
          author: data.author || '',
          time: this.formatTime(data.publishTime || data.createTime),
          source: data.source || '',
          likes: data.likeCount || 0,
          isLiked: data.isLike === 1 || data.isLiked === true,
          isFavorited: data.isFavorite === 1 || data.isFavorited === true,
          image: this.resolveImageUrl(data.coverImage),
          content: data.content || '',
          summary: data.summary || data.description || '',
          id: data.id || '',
          publisherName: data.publisherName || '',
          approvalOneBy: data.approvalOneBy || '',
          approvalTwoBy: data.approvalTwoBy || '',
          approvalThreeBy: data.approvalThreeBy || '',
          publisherPostName: data.publisherPostName || '',
          newsImages: this.mapNewsImages(data.newsImages)
        };
        this.galleryIndex = 0;
        console.log('文章数据加载完成:', this.article.title);

        // 【关键】先完成路由参数替换，确保分享链接和实际 URL 一致
        const categoryId = data.categoryId;
        if (categoryId && Number(this.$route.query.categoryId) !== Number(categoryId)) {
          this.$router.replace({ query: { ...this.$route.query, categoryId } });
        }

        // ============ 新增：iOS 微信强制刷新逻辑 ============
        // iOS 微信客户端在 SPA 内部路由跳转时不会重新抓取页面，
        // 会沿用首次进入（如首页）的分享数据。
        // 通过 location.replace 强制刷新一次，让微信重新抓取。
        if (this.isWeChat() && this.isIOS()) {
          const currentUrl = this.getCurrentShareLink();
          const refreshKey = 'wx_refreshed_' + currentUrl;
          const alreadyRefreshed = sessionStorage.getItem(refreshKey);

          if (!alreadyRefreshed) {
            sessionStorage.setItem(refreshKey, '1');
            // 稍微延迟一下，确保路由 replace 已完成，URL 已稳定
            setTimeout(() => {
              window.location.replace(currentUrl);
            }, 100);
            return; // 直接返回，让页面刷新后再走一遍流程
          }
        }
        // ============ 新增结束 ============

        // 【关键】等路由替换完成 + 微信原生嗅探完成后，再设置分享数据
        // 延迟 800ms，保证 URL 已稳定且文章标题、图片、描述全部就绪
        if (this.isWeChat() && this.article.title) {
          setTimeout(() => {
            if (this.wxConfigReady) {
              this.doForceSetWechatShareData();
            } else {
              // 如果 config 还没完成，等 config 完成后由 onShare 再兜底
              console.log('⏳ wx.config 尚未就绪，分享将在用户点击时兜底设置');
            }
          }, 800);
        }

        this.fetchComments();
      } catch (err) { console.error('新闻加载失败:', err); this.$router.replace({ path: '/404' }); } 
      finally { if (!redirected) this.loading = false; }
    },

    // turnUrlEnabled：0 不跳转，1 跳转；turnUrl：跳转地址
    getTurnUrl(data) {
      if (Number(data.turnUrlEnabled) !== 1) return '';
      const url = String(data.turnUrl || '').trim();
      if (!url) return '';
      return /^https?:\/\//i.test(url) ? url : `https://${url.replace(/^\/+/, '')}`;
    },

    async fetchUserInfo() {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await getUserInfo();
        const data = res.data || {};
        this.userInfo = { nickname: data.nickname || '', avatar: this.resolveImageUrl(data.avatar) };
      } catch (err) { console.error('用户信息获取失败:', err); localStorage.removeItem('token'); }
    },

    async fetchComments() {
      if (!this.article.id) return;
      try {
        const res = await getCommentList({ pageNum: 1, pageSize: 10, newsId: this.article.id });
        const data = res.data || {};
        const list = data.rows || data.list || data.records || [];
        this.comments = list.map(item => this.mapComment(item));
        this.commentTotal = data.total || 0;
      } catch (err) { console.error('评论加载失败:', err); }
    },

    mapNewsImages(list) {
      if (!Array.isArray(list)) return [];
      return list
        .filter(item => item && item.imageUrl)
        .slice()
        .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0))
        .map(item => ({
          id: item.id,
          imageUrl: this.resolveImageUrl(item.imageUrl),
          imageDesc: item.imageDesc || '',
          imageAlt: item.imageAlt || '',
          sortOrder: Number(item.sortOrder) || 0
        }));
    },

    switchGalleryImage(step) {
      const total = this.article.newsImages.length;
      if (!total) return;
      this.galleryIndex = (this.galleryIndex + step + total) % total;
    },
    prevGalleryImage() { this.switchGalleryImage(-1); },
    nextGalleryImage() { this.switchGalleryImage(1); },

    onGalleryTouchStart(e) {
      const touch = e.touches && e.touches[0];
      this.galleryTouch = touch ? { x: touch.clientX, y: touch.clientY } : null;
    },
    onGalleryTouchEnd(e) {
      const start = this.galleryTouch;
      this.galleryTouch = null;
      const touch = e.changedTouches && e.changedTouches[0];
      if (!start || !touch) return;
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      // 横向滑动距离足够大且大于纵向位移时才切换，避免影响页面滚动
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      if (dx < 0) this.nextGalleryImage();
      else this.prevGalleryImage();
    },

    mapComment(item) {
      return {
        id: item.id,
        avatar: this.resolveImageUrl(item.avatar),
        name: item.nickname || item.userName || '匿名用户',
        text: item.content || '',
        time: this.formatTime(item.createTime),
        likes: item.upCount || 0,
        replies: (item.replyList || []).map(reply => this.mapComment(reply))
      };
    },

    async onSendComment(text) {
      if (!this.isLoggedIn) return;
      if (!this.article.id) { alert('文章信息未加载，请稍后再试'); return; }
      try { await addComment({ content: text, newsId: this.article.id }); this.fetchComments(); } 
      catch (err) { console.error('评论发表失败:', err); alert('评论发表失败，请稍后重试'); }
    },

    async onReplyComment({ comment, text }) {
      if (!this.isLoggedIn || !comment.id) return;
      try { await replyComment({ parentId: comment.id, content: text }); this.fetchComments(); } 
      catch (err) { console.error('回复失败:', err); alert('回复失败，请稍后重试'); }
    },

    async goLogin() {
      // 直接跳转到登录页，带上当前页面的 redirect 参数
      // 后端会在登录页处理微信授权并返回 code
      const redirect = this.$route.fullPath;
      this.$router.push(`/login?redirect=${encodeURIComponent(redirect)}`);
    },

    async onLikeArticle(willLike) {
      if (!this.isLoggedIn || !this.article.id) return;
      const status = willLike ? 1 : 0;
      try {
        await toggleLike({ newsId: this.article.id, status });
        this.article.isLiked = willLike;
        this.article.likes += willLike ? 1 : -1;
      } catch (err) { console.error('点赞操作失败:', err); alert('操作失败，请稍后重试'); }
    },

    async onFavoriteArticle(willFavorite) {
      if (!this.isLoggedIn || !this.article.id) return;
      const status = willFavorite ? 1 : 0;
      try {
        await toggleFavorite({ newsId: this.article.id, status });
        this.article.isFavorited = willFavorite;
      } catch (err) {
        const msg = err && (err.message || (err.response && err.response.data && err.response.data.msg));
        if (msg && msg.includes('您已收藏过该文章')) { this.article.isFavorited = true; return; }
        console.error('收藏操作失败:', err); alert('操作失败，请稍后重试');
      }
    },

    formatTime(time) {
      if (!time) return '';
      if (typeof time === 'string' && time.length <= 19) return time;
      try {
        const d = new Date(time);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      } catch (e) { return String(time); }
    },

    resolveImageUrl(url) {
      if (!url) return '';
      if (/^https?:\/\//.test(url)) return url;
      const origin = window.location.origin;
      return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
    }
  }
};
</script>
<style scoped>
.detail-page { --detail-page-width: 804px; --detail-content-width: 755px; --detail-font-xs: 24px; --detail-font-sm: 24px; --detail-font-base: 24px; --detail-font-md: 34px; --detail-font-lg: 28px; --detail-font-xl: 28px; --detail-font-2xl: 36px; --detail-color-white: #ffffff; --detail-color-black: #000000; --detail-color-text-dark: #333333; --detail-color-text-gray: #666666; --detail-color-text-light: #999999; --detail-color-primary: #c00513; width: var(--detail-page-width); min-height: 100vh; background-color: var(--detail-color-white); }
.detail-article { width: var(--detail-content-width); margin: 0 auto; padding-top: 32px; }
.detail-title { color: var(--detail-color-black); font-size: var(--detail-font-2xl); font-family: 'Microsoft YaHei', '微软雅黑', sans-serif; font-weight: 700; line-height: 68px; text-align: center; margin: 0; }
.detail-meta { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; margin-top: 20px; padding-bottom: 20px; border-bottom: 1px solid #eeeeee; color: var(--detail-color-text-light); font-size: var(--detail-font-sm); font-family: 'Microsoft YaHei', '微软雅黑', sans-serif; }
.detail-meta__author { margin-right: 20px; }
.detail-meta__time { margin-right: 20px; }
.detail-meta__source { color: var(--detail-color-text-gray); }
.detail-image { display: block; width: 100%; height: auto; margin: 24px 0; }
.detail-content { margin-top: 24px; margin-bottom: 96px; color: var(--detail-color-text-dark); font-size: var(--detail-font-md); font-family: 'Microsoft YaHei', '微软雅黑', sans-serif; line-height: 50px; text-align: justify; }
.detail-content >>> p { margin: 0 0 32px;  }
.detail-content >>> img { display: block; width: 100%; height: auto; margin-bottom: 32px; }

/* 组图轮播（newsImages） */
.detail-gallery { width: 100%; margin-top: 48px; -webkit-user-select: none; user-select: none; }
.detail-gallery__stage { position: relative; width: 100%; height: 460px; overflow: hidden; border-radius: 8px; background-color: #f7f8fa; touch-action: pan-y; }
.detail-gallery__track { display: flex; height: 100%; margin: 0; padding: 0; list-style: none; transition: transform 0.35s ease; will-change: transform; }
.detail-gallery__slide { flex: 0 0 100%; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
.detail-gallery__image { display: block; width: 100%; height: 100%; object-fit: contain; background-color: #f7f8fa; }
.detail-gallery__arrow { position: absolute; top: 50%; z-index: 2; width: 64px; height: 64px; margin-top: -32px; border-radius: 50%; background-color: rgba(0, 0, 0, 0.35); color: var(--detail-color-white); font-size: 44px; line-height: 58px; text-align: center; cursor: pointer; }
.detail-gallery__arrow:active { background-color: rgba(0, 0, 0, 0.55); }
.detail-gallery__arrow--prev { left: 16px; }
.detail-gallery__arrow--next { right: 16px; }
.detail-gallery__caption { margin-top: 20px; color: var(--detail-color-text-gray); font-size: var(--detail-font-sm); font-family: 'Microsoft YaHei', '微软雅黑', sans-serif; line-height: 44px; text-align: justify; }
.detail-gallery__caption >>> p { margin: 0 0 12px; }
.detail-gallery__caption >>> img { max-width: 100%; height: auto; }
.detail-gallery__pager { display: flex; align-items: center; justify-content: center; margin-top: 16px; color: var(--detail-color-text-gray); font-size: var(--detail-font-sm); font-family: 'Microsoft YaHei', '微软雅黑', sans-serif; }
.detail-gallery__pager-arrow { width: 52px; height: 52px; margin: 0 24px; border: 1px solid #dddddd; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 34px; color: var(--detail-color-text-gray); cursor: pointer; }
.detail-gallery__pager-arrow:active { background-color: #f2f2f2; }
.detail-gallery__pager-text { letter-spacing: 2px; }

/* 信息发布和审核人员 */
.detail-publish-info {
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--detail-color-text-gray);
  font-size: var(--detail-font-sm);
  font-family: 'Microsoft YaHei', '微软雅黑', sans-serif;
  justify-content: flex-end;
}

.detail-publish-info__item {
  color: var(--detail-color-text-gray);
}

.detail-publish-info__item strong {
  color: var(--detail-color-text-dark);
  font-weight: 600;
}

.detail-publish-info__divider {
  color: #dddddd;
  font-size: var(--detail-font-xs);
}
.detail-comments { width: var(--detail-content-width); margin: 48px auto 0; padding-bottom: 110px; }
.detail-comments__title { color: var(--detail-color-black); font-size: var(--detail-font-xl); font-family: 'Microsoft YaHei', '微软雅黑', sans-serif; font-weight: 700; margin: 0 0 16px; }
.detail-page >>> .comment-item__name, .detail-page >>> .share-bar__name, .detail-page >>> .comment-input__field, .detail-page >>> .comment-input__send { font-size: var(--detail-font-sm); }
.detail-page >>> .comment-item__text { font-size: var(--detail-font-base); line-height: 52px; }
.detail-page >>> .comment-item__footer { font-size: var(--detail-font-xs); }
.detail-page >>> .comment-input__like-count { font-size: var(--detail-font-xs); }
.detail-page >>> .comment-item__like-icon { color: #cccccc; }
.detail-page >>> .comment-input__like-icon, .detail-page >>> .comment-input__favorite-icon, .detail-page >>> .comment-input__share-icon { color: #666666; }
.detail-page >>> .comment-input__like-icon--active { color: #b0000b; }
.detail-page >>> .comment-input__favorite-icon--active { color: #ff9900; }
.detail-page >>> .comment-input { position: fixed; bottom: 0; left: 50%; transform: translateX(-50%); width: 100%; max-width: var(--detail-page-width); z-index: 9999; }
.share-guide-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.75); z-index: 99999; cursor: pointer; }
.share-guide-arrow-wrapper { position: absolute; top: 30px; right: 30px; display: flex; flex-direction: column; align-items: flex-end; animation: shareGuidePulse 1.5s ease-in-out infinite; }
.share-guide-arrow { width: 0; height: 0; border-left: 35px solid transparent; border-right: 35px solid transparent; border-bottom: 50px solid #ff6b35; margin-right: 30px; transform: rotate(20deg); filter: drop-shadow(0 4px 20px rgba(255, 107, 53, 0.6)); }
.share-guide-text { background: #ff6b35; color: white; padding: 18px 35px; border-radius: 16px; font-size: 32px; font-weight: bold; margin-top: 12px; white-space: nowrap; box-shadow: 0 8px 30px rgba(255, 107, 53, 0.5); letter-spacing: 2px; }
.share-guide-icon { display: inline-block; background: rgba(255, 255, 255, 0.25); padding: 0 12px; border-radius: 8px; font-size: 38px; letter-spacing: 4px; font-weight: 900; }
.share-guide-tip { position: absolute; bottom: 80px; left: 50%; transform: translateX(-50%); color: rgba(255, 255, 255, 0.7); font-size: 20px; letter-spacing: 3px; background: rgba(0, 0, 0, 0.3); padding: 10px 30px; border-radius: 30px; }
@keyframes shareGuidePulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.06); opacity: 0.92; } }
@media (max-width: 768px) { .detail-gallery__stage { height: 380px; } .detail-gallery__arrow { width: 56px; height: 56px; margin-top: -28px; font-size: 38px; line-height: 50px; } .detail-gallery__arrow--prev { left: 12px; } .detail-gallery__arrow--next { right: 12px; } .detail-gallery__caption { margin-top: 16px; line-height: 40px; } .detail-gallery__pager-arrow { width: 46px; height: 46px; margin: 0 18px; font-size: 30px; } }
@media (max-width: 768px) { .share-guide-arrow-wrapper { top: 25px; right: 20px; } .share-guide-arrow { border-left-width: 28px; border-right-width: 28px; border-bottom-width: 40px; margin-right: 20px; } .share-guide-text { font-size: 26px; padding: 14px 28px; } .share-guide-icon { font-size: 30px; } .share-guide-tip { font-size: 17px; bottom: 60px; padding: 8px 24px; } }
@media (max-width: 480px) { .share-guide-arrow-wrapper { top: 20px; right: 15px; } .share-guide-arrow { border-left-width: 22px; border-right-width: 22px; border-bottom-width: 32px; margin-right: 15px; } .share-guide-text { font-size: 20px; padding: 12px 20px; border-radius: 12px; } .share-guide-icon { font-size: 24px; } .share-guide-tip { font-size: 14px; bottom: 50px; padding: 6px 18px; } }
</style>